import { supabase } from '../config/supabase.js';
export class FaxingService {
    /**
     * Create a fax record for prescription delivery to pharmacy
     * This will trigger the automatic order creation via database trigger
     */
    static async sendPrescriptionFax(data) {
        console.log('🔍 FAXING SERVICE: Sending prescription fax with data:', JSON.stringify(data, null, 2));
        try {
            // Map service interface to API schema
            const apiPayload = {
                patient_id: data.patient_id,
                provider_id: data.provider_profile_id, // Map provider_profile_id -> provider_id
                medication_preference_id: data.approval_id, // This is the medication preference ID
                approval_id: data.approval_id, // Also pass as approval_id for trigger compatibility
                fax_number: data.fax_number,
                pharmacy_name: data.pharmacy_name,
                prescription_details: `Prescription for patient ${data.patient_id}` // Default prescription details
            };
            console.log('🔍 FAXING SERVICE: Calling /api/faxes/send endpoint...');
            // Use API endpoint instead of direct Supabase call
            const response = await fetch('/api/faxes/send', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(apiPayload)
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('❌ FAXING SERVICE: API call failed:', response.status, errorData);
                return {
                    success: false,
                    error: errorData.error || `API call failed with status ${response.status}`
                };
            }
            const result = await response.json();
            if (result.error) {
                console.error('❌ FAXING SERVICE: API returned error:', result.error);
                return {
                    success: false,
                    error: result.error
                };
            }
            console.log('✅ FAXING SERVICE: Fax record created successfully via API:', result.data?.id);
            console.log('🤖 FAXING SERVICE: This should trigger automatic order creation via database trigger');
            return {
                success: true,
                data: result.data
            };
        }
        catch (error) {
            console.error('❌ FAXING SERVICE: Exception occurred:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }
    /**
     * Update fax status (e.g., from pending to sent to delivered)
     */
    static async updateFaxStatus(faxId, status) {
        console.log(`🔍 FAXING SERVICE: Updating fax ${faxId} status to: ${status}`);
        try {
            const updateData = {
                status,
                updated_at: new Date().toISOString()
            };
            // Add timestamp fields based on status
            if (status === 'sent') {
                updateData.sent_at = new Date().toISOString();
            }
            else if (status === 'delivered') {
                updateData.delivered_at = new Date().toISOString();
            }
            const { data: fax, error } = await supabase
                .from('faxes')
                .update(updateData)
                .eq('id', faxId)
                .select()
                .single();
            if (error) {
                console.error('❌ FAXING SERVICE: Error updating fax status:', error);
                return {
                    success: false,
                    error: `Failed to update fax status: ${error.message}`
                };
            }
            console.log('✅ FAXING SERVICE: Fax status updated successfully');
            return {
                success: true,
                data: fax
            };
        }
        catch (error) {
            console.error('❌ FAXING SERVICE: Exception updating fax status:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }
    /**
     * Get fax records for a patient
     */
    static async getPatientFaxes(patientId) {
        console.log('🔍 FAXING SERVICE: Getting faxes for patient:', patientId);
        try {
            const { data: faxes, error } = await supabase
                .from('faxes')
                .select('*')
                .eq('patient_id', patientId)
                .order('created_at', { ascending: false });
            if (error) {
                console.error('❌ FAXING SERVICE: Error getting patient faxes:', error);
                return {
                    success: false,
                    error: `Failed to get patient faxes: ${error.message}`
                };
            }
            console.log('✅ FAXING SERVICE: Retrieved patient faxes:', faxes?.length || 0);
            return {
                success: true,
                data: faxes || []
            };
        }
        catch (error) {
            console.error('❌ FAXING SERVICE: Exception getting patient faxes:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }
    /**
     * Get fax records for a provider
     */
    static async getProviderFaxes(providerProfileId) {
        console.log('🔍 FAXING SERVICE: Getting faxes for provider:', providerProfileId);
        try {
            const { data: faxes, error } = await supabase
                .from('faxes')
                .select('*')
                .eq('provider_profile_id', providerProfileId)
                .order('created_at', { ascending: false });
            if (error) {
                console.error('❌ FAXING SERVICE: Error getting provider faxes:', error);
                return {
                    success: false,
                    error: `Failed to get provider faxes: ${error.message}`
                };
            }
            console.log('✅ FAXING SERVICE: Retrieved provider faxes:', faxes?.length || 0);
            return {
                success: true,
                data: faxes || []
            };
        }
        catch (error) {
            console.error('❌ FAXING SERVICE: Exception getting provider faxes:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }
    /**
     * Get a specific fax by ID
     */
    static async getFaxById(faxId) {
        console.log('🔍 FAXING SERVICE: Getting fax by ID:', faxId);
        try {
            const { data: fax, error } = await supabase
                .from('faxes')
                .select('*')
                .eq('id', faxId)
                .single();
            if (error) {
                if (error.code === 'PGRST116') {
                    // No rows returned
                    console.log('🔍 FAXING SERVICE: Fax not found');
                    return {
                        success: true,
                        data: null
                    };
                }
                console.error('❌ FAXING SERVICE: Error getting fax by ID:', error);
                return {
                    success: false,
                    error: `Failed to get fax: ${error.message}`
                };
            }
            console.log('✅ FAXING SERVICE: Retrieved fax successfully');
            return {
                success: true,
                data: fax
            };
        }
        catch (error) {
            console.error('❌ FAXING SERVICE: Exception getting fax by ID:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }
    /**
     * Generate prescription fax from clinical note and medication approval
     * This is the main workflow trigger that connects clinical notes to order creation
     */
    static async generatePrescriptionFromClinicalNote(clinicalNoteId, appointmentId, pharmacyFaxNumber, pharmacyName) {
        console.log('🔍 FAXING SERVICE: Generating prescription fax from clinical note:', {
            clinicalNoteId,
            appointmentId,
            pharmacyFaxNumber,
            pharmacyName
        });
        try {
            // First, get the clinical note details
            const { data: clinicalNote, error: noteError } = await supabase
                .from('clinical_notes')
                .select('*')
                .eq('id', clinicalNoteId)
                .single();
            if (noteError || !clinicalNote) {
                console.error('❌ FAXING SERVICE: Failed to get clinical note:', noteError);
                return {
                    success: false,
                    error: 'Clinical note not found'
                };
            }
            // Get the appointment to find associated medication approvals
            const { data: appointment, error: appointmentError } = await supabase
                .from('appointments')
                .select('*')
                .eq('id', appointmentId)
                .single();
            if (appointmentError || !appointment) {
                console.error('❌ FAXING SERVICE: Failed to get appointment:', appointmentError);
                return {
                    success: false,
                    error: 'Appointment not found'
                };
            }
            // Find approved medication for this patient
            const { data: approvals, error: approvalsError } = await supabase
                .from('medication_approvals')
                .select(`
          *,
          patient_medication_preferences (
            *,
            medications (*)
          )
        `)
                .eq('provider_id', appointment.provider_id)
                .eq('status', 'approved');
            if (approvalsError) {
                console.error('❌ FAXING SERVICE: Failed to get medication approvals:', approvalsError);
                return {
                    success: false,
                    error: 'Failed to get medication approvals'
                };
            }
            // Filter approvals for this specific patient
            const patientApprovals = approvals?.filter(approval => {
                return approval.patient_medication_preferences?.patient_id === clinicalNote.patient_id;
            }) || [];
            if (patientApprovals.length === 0) {
                console.log('⚠️ FAXING SERVICE: No approved medications found for patient');
                return {
                    success: false,
                    error: 'No approved medications found for this patient'
                };
            }
            // Use the first approved medication (in real system, might need provider selection)
            const approval = patientApprovals[0];
            console.log('🔍 FAXING SERVICE: Using medication approval:', approval.id);
            // Get patient and provider profile IDs
            const { data: patient, error: patientError } = await supabase
                .from('patients')
                .select('profile_id')
                .eq('id', clinicalNote.patient_id)
                .single();
            const { data: provider, error: providerError } = await supabase
                .from('providers')
                .select('profile_id')
                .eq('id', clinicalNote.provider_id)
                .single();
            if (patientError || !patient || providerError || !provider) {
                console.error('❌ FAXING SERVICE: Failed to get patient/provider profiles');
                return {
                    success: false,
                    error: 'Failed to get patient or provider profiles'
                };
            }
            // Create fax record - this will trigger the automatic order creation
            const faxResult = await FaxingService.sendPrescriptionFax({
                patient_id: clinicalNote.patient_id,
                approval_id: approval.id,
                provider_profile_id: provider.profile_id,
                patient_profile_id: patient.profile_id,
                fax_number: pharmacyFaxNumber,
                pharmacy_name: pharmacyName
            });
            if (!faxResult.success || !faxResult.data) {
                return {
                    success: false,
                    error: faxResult.error || 'Failed to create fax'
                };
            }
            console.log('✅ FAXING SERVICE: Prescription fax generated successfully');
            console.log('🤖 FAXING SERVICE: Database trigger should have created medication order automatically');
            return {
                success: true,
                data: {
                    fax: faxResult.data,
                    triggeredOrderCreation: true
                }
            };
        }
        catch (error) {
            console.error('❌ FAXING SERVICE: Exception generating prescription fax:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }
}
